import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useTable } from "react-table";
import middleware from "../../../Api/Middleware";
import TablePreset from "../../../Table/TablePreset";
import "../../../Table/Table.css";
import "../../../Orders/Orders.css";
import { useNavigate, useParams } from "react-router";
import { format } from 'date-fns'
import { nl } from 'date-fns/locale';
import TDSTextNavigation from "../../../Templates/TDSTextNavigation";
import CheckboxHeader from "../../../Table/TableComponents/CheckboxHeader";
import CheckboxCell from "../../../Table/TableComponents/CheckboxCell";
import SortableHeader from "../../../Table/TableComponents/SortableHeader";
import { GetOrderStatus, GetPaymentStatus, GetDeliveryStatus } from "../../../Orders/Orders";
import { formatNumber } from "../../../../utils";

const OrdersTab = () => {
  const navigate = useNavigate();
  const { internal_id } = useParams();
  const [loading, setLoading] = useState(false);

  const [consumers, setConsumers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [salesChannel, setSalesChannel] = useState([]);
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [orders, setOrders] = useState([]);
  const [orderIds, setOrderIds] = useState([]);
  const [pagination, setPagination] = useState({
    "page_size": 10,
    "page": 1,
    "max_items": 0,
    "begin_of_page": 0,
    "end_of_page": 0,
  });
  const [order, setOrder] = useState({ orderBy: '', direction: '' });
  const [searchBar, setSearchBar] = useState("");
  const [timeoutId, setTimeoutId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const searchQuery = searchBar.trim() ? `&searchTerm=${encodeURIComponent(searchBar.trim())}&noSearch=["delivery_status","order_id", "order_status", "payment_status", "sales_channel_id", "price_excl","created_at"]` : "";
      const [ordersResponse] = await Promise.all([
        middleware.get(`sp/consumerOrders?internal_id=${internal_id}&page=${pagination.page}&page_size=${pagination.page_size}&${order.orderBy !== '' && order.direction !== '' ? `&orderBy=${order.orderBy}&direction=${order.direction}` : ''}${searchBar ? `&searchTerm=${searchBar}` : ''}`),
      ]);
      setOrders(ordersResponse.data.data);

      setOrderIds(ordersResponse.data.data.map(data => data.order_id));
      setPagination({ ...pagination, "max_items": ordersResponse.data.total, "begin_of_page": ordersResponse.data.from, "end_of_page": ordersResponse.data.to });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleSearchChange = useCallback((value) => {
    setSearchBar(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  useEffect(() => {
    try {
      fetchAll();
    } catch (error) {
      console.warn(error)
    }
  }, [pagination.page_size, pagination.page, order, order.direction, searchBar]);


  const [selectedIds, setSelectedIds] = useState([]);

  // Handle checkbox selection
  const handleSelection = (productId, isSelected) => {
    if (isSelected) {
      setSelectedIds(previous => [...previous, productId]);
    } else {
      setSelectedIds(previous => previous.filter(id => id !== productId));
    }
  };

  // Columns for the table
  const columns = useMemo(
    () => [
      {
        Header: (
          <CheckboxHeader currentItem={orders} selectedIds={selectedIds} setSelectedIds={setSelectedIds} pk={'order_id'}>
          </CheckboxHeader>
        ),
        accessor: "select",
        Cell: ({ row }) => {
          return <CheckboxCell row={row} handleSelection={handleSelection} selectedIds={selectedIds} pk={'order_id'} />
        }
      },
      {
        Header: () => (
          <SortableHeader currentItem={order} setItem={setOrder} pos={"start"} field="order_number">
            Bestelling
          </SortableHeader>
        ),
        accessor: 'order_number',
        Cell: ({ row }) => {
          return (
            <div className="d-flex flex-column">
              <TDSTextNavigation
                text={row.original.order_number}
                link={`/bestellingen/overzicht/${row.original.order_number}`}
                extraClassNames={"text-nowrap"}
                openNewTab={true}
              />
              <div>
                <label className="d-flex flex-column"><span>{row.original.created_date}</span> <span>{row.original.created_time}</span></label>
              </div>
            </div>
          )
        }
      },
      {
        Header: () => (
          <SortableHeader currentItem={order} setItem={setOrder} field="price_incl">
            Prijs incl.
          </SortableHeader>
        ),
        accessor: 'price_incl',
        Cell: ({ row }) => {
          return (
            <div className="d-flex flex-column text-center">
              <div>€ {row.original.total_price_incl_tax}</div>
            </div>
          );
        }
      },
      {
        Header: () => (
          <SortableHeader currentItem={order} setItem={setOrder} field="sales_channel_id">
            Verkooplocatie
          </SortableHeader>
        ),
        accessor: 'sales_channel_id',
        Cell: ({ row }) => {
          return (
            <div className="text-center">{row.original.sales_channel_name}</div>
          )
        }
      },
      {
        Header: () => (
          <SortableHeader currentItem={order} setItem={setOrder} field="order_status">
            Status
          </SortableHeader>
        ),
        accessor: 'order_status',
        Cell: ({ row }) => {
          return <div className={`text-center ${row.original.order_status == 0 ? 'text-danger' : 'text-success'}`}>{<GetOrderStatus orderStatus={row.original.order_status}/>}</div>;
        }
      },
      {
        Header: () => (
          <SortableHeader currentItem={order} setItem={setOrder} field="payment_status">
            Betaling
          </SortableHeader>
        ),
        accessor: 'payment_status',
        Cell: ({ row }) => {
          return (
            <div className={`text-center ${row.original.payment_status == 0 ? 'text-danger' : 'text-success'}`}>{<GetPaymentStatus paymentStatus={row.original.payment_status}/>}</div>
          )
        }
      },
      {
        Header: () => (
          <SortableHeader currentItem={order} setItem={setOrder} field="delivery_status">
            Verzending
          </SortableHeader>
        ),
        accessor: 'delivery_status',
        Cell: ({ row }) => {
          return (
            <div className={`text-center ${row.original.delivery_status == 0 ? 'text-danger' : 'text-success'}`}>{<GetDeliveryStatus deliveryStatus={row.original.delivery_status}/>}</div>
          )
        }
      },
    ], [order.orderBy, order.direction, selectedIds, setSelectedIds, orders]
  );

  // Table instance
  const tableInstance = useTable({ columns, data: orders });


  return (
    <div className="ordersOverviewTab h-100 mt-4">
      <div className="mb-3 componentMarginTop2_5 tablesCustomerEdit  h-100">
        <TablePreset
          data={[{ 'selectedIds': selectedIds, 'tableInstance': tableInstance, 'pagination': pagination, 'primary_key': 'order_id' }]}
          setPagination={setPagination}
          handleSelection={handleSelection}
          searchBar={[{ shown: true, searchFunction: handleSearchChange, bordered: true}]}
          rightButtons={[{ title: "Nieuwe bestelling", function: () => window.open(`/bestellingen/nieuw?u=${internal_id}`) }]}
        />
      </div>
    </div>
  );
}

export default OrdersTab;